import type { Metric } from 'web-vitals';
import { GA4_MEASUREMENT_ID } from '../constants/analytics';

export const CONSENT_STORAGE_KEY = 'cookies-preferences';
export const CONSENT_ACCEPTED_KEY = 'cookies-accepted';

export type CookiePreferences = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function readPreferences(): CookiePreferences | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookiePreferences;
    return {
      necessary: parsed.necessary !== false,
      analytics: parsed.analytics === true,
      marketing: parsed.marketing === true,
    };
  } catch {
    return null;
  }
}

export function getCookiePreferences(): CookiePreferences | null {
  return readPreferences();
}

export function hasAnalyticsConsent(): boolean {
  return readPreferences()?.analytics === true;
}

export function getGaMeasurementId(): string {
  return GA4_MEASUREMENT_ID;
}

/** Stub gtag + Consent Mode (aucun chargement Google avant consentement). */
export function initAnalyticsConsentStub(): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer?.push(args);
    };
  }
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500,
  });
}

function updateGoogleConsent(granted: boolean): void {
  initAnalyticsConsentStub();
  window.gtag?.('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
}

export function initGoogleAnalytics(): void {
  if (typeof window === 'undefined') return;
  if (!hasAnalyticsConsent()) return;

  const measurementId = getGaMeasurementId();
  initAnalyticsConsentStub();
  updateGoogleConsent(true);

  if (!window.gtag) return;

  if (!document.querySelector(`script[data-ga-id="${measurementId}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    script.dataset.gaId = measurementId;
    document.head.appendChild(script);
  }

  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false,
    anonymize_ip: true,
    cookie_flags: 'SameSite=None;Secure',
  });
}

export function disableGoogleAnalytics(): void {
  updateGoogleConsent(false);
}

export function trackPageView(path: string): void {
  if (!hasAnalyticsConsent() || !window.gtag) return;
  const measurementId = getGaMeasurementId();
  const pagePath = path.startsWith('/') ? path : `/${path}`;
  window.gtag('event', 'page_view', {
    send_to: measurementId,
    page_path: pagePath,
    page_location: `${window.location.origin}${pagePath}${window.location.search}`,
    page_title: document.title,
  });
}

export type ConversionEventName =
  | 'form_submit'
  | 'contact_click';

export function trackConversion(
  eventName: ConversionEventName,
  params: Record<string, string | number | boolean | undefined> = {},
): void {
  if (!hasAnalyticsConsent() || !window.gtag) return;
  const measurementId = getGaMeasurementId();
  const payload: Record<string, unknown> = { send_to: measurementId };
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') payload[key] = value;
  }
  window.gtag('event', eventName, payload);
}

export function sendWebVitalToGa(metric: Metric): void {
  if (!hasAnalyticsConsent() || !window.gtag) return;
  window.gtag('event', metric.name, {
    event_category: 'Web Vitals',
    event_label: metric.id,
    value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
    non_interaction: true,
  });
}

export function onCookieConsentUpdated(preferences?: CookiePreferences | null): void {
  const prefs = preferences ?? readPreferences();
  const granted = prefs?.analytics === true;
  if (granted) {
    initGoogleAnalytics();
    trackPageView(`${window.location.pathname}${window.location.search}`);
  } else {
    disableGoogleAnalytics();
  }
}
